import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatDate, timeAgo, cn } from "@/lib/utils";
import {
  Globe2, Plus, Lock, Trophy, Search, ShieldCheck, Building2,
  Calendar, Wallet, ArrowUpRight, Gavel, FileSearch, Sparkles, Crown,
} from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?auto=format&fit=crop&w=1600&q=80";

/** Free-tier teaser: aggregate stats only, no individual listings (PRD §2/§8.16). */
function PreviewBanner() {
  const [stats, setStats] = useState(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    api.get("/marketplace/preview").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const upgrade = () => {
    setUpgrading(true);
    api.post("/org/upgrade-premium").then(() => window.location.reload()).finally(() => setUpgrading(false));
  };

  return (
    <div
      className="rounded-3xl border border-slate-200 text-white p-10 sm:p-14 text-center space-y-6 shadow-xl shadow-blue-950/10 bg-cover bg-center relative overflow-hidden"
      style={{ backgroundImage: `linear-gradient(to bottom, rgba(11,30,61,.9), rgba(18,58,110,.94)), url(${HERO_IMAGE})` }}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="hero-glow-a absolute -top-16 right-10 h-72 w-72 rounded-full bg-orange-400/25 blur-[90px]" />
        <div className="hero-glow-b absolute top-1/3 -right-20 h-64 w-64 rounded-full bg-red-500/20 blur-[90px]" />
        <div className="hero-glow-c absolute -bottom-24 left-10 h-72 w-72 rounded-full bg-emerald-400/20 blur-[90px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.07)_1px,transparent_0)] bg-[size:22px_22px]" />
      </div>
      <div className="relative font-display text-2xl font-bold tracking-tight">
        <span className="text-blue-400">Marketplace</span>
      </div>
      <div className="relative inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium uppercase tracking-[0.2em] text-blue-200">
        <Lock className="h-3.5 w-3.5" /> Premium
      </div>
      <h2 className="relative font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-xl mx-auto">
        Turn your J-Score into external business
      </h2>
      <p className="text-blue-100/90 max-w-lg mx-auto text-[15px]">
        Post tenders to verified organizations, bid on external work, and build a public
        marketplace rank — the same trust economy that runs your internal board, opened outward.
      </p>
      {stats && (
        <div className="flex justify-center gap-10 pt-2">
          {[
            ["Completed this month", stats.tenders_completed_this_month],
            ["Open right now", stats.tenders_open_now],
            ["Orgs active (30d)", stats.orgs_active_last_30d],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="font-mono text-3xl font-bold">{value}</div>
              <div className="text-xs text-blue-200/80 mt-1">{label}</div>
            </div>
          ))}
        </div>
      )}
      <Button
        size="lg"
        className="bg-white text-[#0b1e3d] hover:bg-blue-50 font-semibold"
        onClick={upgrade}
        disabled={upgrading}
        data-testid="upgrade-premium-btn"
      >
        {upgrading ? "Upgrading…" : "Upgrade to Premium"}
      </Button>
    </div>
  );
}

function StatPill({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="font-mono text-2xl sm:text-3xl font-bold text-white">{value}</span>
      <span className="text-xs text-blue-200/80 mt-0.5">{label}</span>
    </div>
  );
}

function Hero({ org, stats }) {
  return (
    <div
      className="rounded-3xl text-white p-8 sm:p-12 shadow-xl shadow-blue-950/10 relative overflow-hidden bg-cover bg-center"
      style={{ backgroundImage: `linear-gradient(120deg, rgba(8,20,44,.94), rgba(15,42,82,.88) 55%, rgba(18,58,110,.8)), url(${HERO_IMAGE})` }}
    >
      {/* Brand-colored glow blobs, drifting slowly — pulled from the Jamoora mark's ring colors */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="hero-glow-a absolute -top-16 right-10 h-72 w-72 rounded-full bg-orange-400/25 blur-[90px]" />
        <div className="hero-glow-b absolute top-1/3 -right-20 h-64 w-64 rounded-full bg-red-500/20 blur-[90px]" />
        <div className="hero-glow-c absolute -bottom-24 left-10 h-72 w-72 rounded-full bg-emerald-400/20 blur-[90px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.07)_1px,transparent_0)] bg-[size:22px_22px]" />
        <div className="hero-sweep absolute top-0 -left-1/2 h-full w-1/3 bg-gradient-to-r from-transparent via-white/10 to-transparent" />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.3em] text-blue-200">
          <ShieldCheck className="h-3.5 w-3.5" /> Jamoora
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-3">
          <span className="text-blue-400">Marketplace</span>
        </h1>
        <p className="mt-2 max-w-xl text-lg sm:text-xl font-display font-medium">
          <span className="text-orange-300">Ideas.</span>{" "}
          <span className="text-red-300">Solutions.</span>{" "}
          <span className="text-emerald-300">Opportunities.</span>
        </p>
        <p className="text-blue-100/90 mt-3 max-w-lg text-[15px]">
          {org?.name || "Your organization"} has Premium access on Jamoora — a trusted place
          for verified organizations to post tenders, bid on external work, and build a public
          track record.
        </p>

        <div className="flex flex-wrap gap-8 sm:gap-12 mt-8">
          <StatPill label="Open tenders now" value={stats?.tenders_open_now ?? "—"} />
          <StatPill label="Completed this month" value={stats?.tenders_completed_this_month ?? "—"} />
          <StatPill label="Orgs active (30d)" value={stats?.orgs_active_last_30d ?? "—"} />
        </div>
      </div>
    </div>
  );
}

const ACTION_CARDS = [
  {
    key: "post", icon: Plus, tone: "bg-blue-50 text-blue-600",
    title: "Post a Tender", body: "Need external capacity? Post your requirement and get bids from verified organizations.",
    cta: "Create tender", requiresPost: true, action: { type: "navigate", to: "/app/marketplace/new" },
  },
  {
    key: "bid", icon: Gavel, tone: "bg-violet-50 text-violet-600",
    title: "Bid on a Tender", body: "Browse open tenders from other organizations and submit competitive bids.",
    cta: "Browse tenders", action: { type: "tab", tab: "open" },
  },
  {
    key: "mine", icon: FileSearch, tone: "bg-emerald-50 text-emerald-600",
    title: "My Org's Tenders", body: "Track everything you've posted — awards, proof review, and disputes in one place.",
    cta: "View my tenders", action: { type: "tab", tab: "mine" },
  },
  {
    key: "leaderboard", icon: Trophy, tone: "bg-amber-50 text-amber-600",
    title: "View Leaderboard", body: "See top-ranked organizations by category, based on real completion history.",
    cta: "Go to leaderboard", action: { type: "navigate", to: "/app/marketplace/leaderboard" },
  },
];

function ActionCard({ card, disabled, onClick }) {
  const Icon = card.icon;
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={`action-card-${card.key}`}
      className={cn(
        "text-left rounded-2xl border border-slate-200 bg-white p-5 transition-all group",
        disabled ? "opacity-50 cursor-not-allowed" : "hover:border-blue-300 hover:shadow-md"
      )}
    >
      <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center mb-3", card.tone)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-semibold text-slate-900">{card.title}</div>
      <p className="text-xs text-slate-500 mt-1 leading-relaxed">{card.body}</p>
      <div className="text-xs font-medium text-blue-600 mt-3 flex items-center gap-1 group-hover:gap-1.5 transition-all">
        {card.cta} <ArrowUpRight className="h-3.5 w-3.5" />
      </div>
    </button>
  );
}

function FeedRow({ t }) {
  return (
    <Link to={`/app/marketplace/tenders/${t.id}`} data-testid={`tender-card-${t.id}`}>
      <div className="flex items-start gap-4 px-5 py-4 hover:bg-slate-50 transition-colors rounded-xl group">
        <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
          <Building2 className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>{timeAgo(t.created_at)}</span>
            <Badge variant="outline" className="capitalize">{t.status.replace(/_/g, " ")}</Badge>
          </div>
          <h3 className="font-semibold text-slate-900 mt-0.5 group-hover:text-blue-700 transition-colors truncate">{t.title}</h3>
          <p className="text-sm text-slate-600 line-clamp-1 mt-0.5">{t.description}</p>
          <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5">
            <span className="text-xs font-medium text-slate-700">{t.poster_org_name}</span>
            {t.poster_org_premium && <ShieldCheck className="h-3 w-3 text-emerald-500" title="Verified" />}
            <span className="text-slate-300">·</span>
            <Badge variant="secondary">{t.category}</Badge>
            {t.bid_count > 0 && (
              <span className="text-xs text-slate-400">{t.bid_count} bid{t.bid_count === 1 ? "" : "s"}</span>
            )}
          </div>
        </div>
        <div className="text-right shrink-0 hidden sm:block">
          <div className="flex items-center gap-1 text-sm font-mono font-semibold text-slate-900 justify-end">
            <Wallet className="h-3.5 w-3.5 text-slate-400" /> {t.budget_value || "—"}
          </div>
          <div className="flex items-center gap-1 text-xs text-slate-400 justify-end mt-0.5">
            <Calendar className="h-3 w-3" /> {t.due_date ? formatDate(t.due_date) : "No due date"}
          </div>
          <Button size="sm" variant="outline" className="mt-2">View details</Button>
        </div>
      </div>
    </Link>
  );
}

function LeaderboardWidget({ myOrgId }) {
  const [board, setBoard] = useState(null);

  useEffect(() => {
    api.get("/marketplace/leaderboard").then((r) => setBoard(r.data)).catch(() => {});
  }, []);

  const top5 = board?.board?.slice(0, 5) || [];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm flex items-center gap-1.5"><Trophy className="h-4 w-4 text-amber-500" /> Leaderboard</h3>
        <Link to="/app/marketplace/leaderboard" className="text-xs text-blue-600 hover:underline">View all</Link>
      </div>
      {!board ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : top5.length === 0 ? (
        <p className="text-xs text-slate-400">No ranked organizations yet.</p>
      ) : (
        <div className="space-y-1">
          {top5.map((row) => (
            <div
              key={row.org_id}
              className={cn(
                "flex items-center gap-2.5 rounded-lg px-2 py-1.5",
                row.org_id === myOrgId && "bg-blue-50 ring-1 ring-inset ring-blue-200"
              )}
            >
              <span className={cn(
                "h-5 w-5 rounded-full text-[10px] font-mono font-semibold flex items-center justify-center shrink-0",
                row.rank_position === 1 ? "bg-amber-100 text-amber-700"
                  : row.rank_position === 2 ? "bg-slate-200 text-slate-600"
                  : row.rank_position === 3 ? "bg-orange-100 text-orange-700"
                  : "bg-slate-100 text-slate-400"
              )}>
                {row.rank_position}
              </span>
              <span className="text-xs font-medium text-slate-700 truncate flex-1">{row.org_name}</span>
              <span className="text-xs font-mono text-slate-500">{row.rank_score}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-4 rounded-xl bg-gradient-to-br from-amber-50 to-white border border-amber-100 p-3 flex items-start gap-2">
        <Crown className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-slate-600">Get more awards and complete on time to climb the leaderboard.</p>
      </div>
    </div>
  );
}

export default function Marketplace() {
  const { org, user } = useAuth();
  const navigate = useNavigate();
  const [tenders, setTenders] = useState([]);
  const [tab, setTab] = useState("open");
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [previewStats, setPreviewStats] = useState(null);

  const isPremium = !!org?.is_premium;
  const canPost = isPremium && ["owner", "department_admin"].includes(user?.role);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    api.get("/marketplace/preview").then((r) => setPreviewStats(r.data)).catch(() => {});
  }, [isPremium]);

  useEffect(() => {
    if (!isPremium) { setLoading(false); return; }
    setLoading(true);
    api.get("/marketplace/tenders", { params: tab === "mine" ? { mine: true } : {} })
      .then((r) => setTenders(r.data))
      .finally(() => setLoading(false));
  }, [isPremium, tab]);

  const filtered = useMemo(() => {
    if (!q.trim()) return tenders;
    const needle = q.trim().toLowerCase();
    return tenders.filter(
      (t) => t.title.toLowerCase().includes(needle) || t.category.toLowerCase().includes(needle)
    );
  }, [tenders, q]);

  if (!isPremium) {
    return <div className="max-w-3xl mx-auto"><PreviewBanner /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      <Hero org={org} stats={previewStats} />

      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Get started</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ACTION_CARDS.map((card) => (
            <ActionCard
              key={card.key}
              card={card}
              disabled={card.requiresPost && !canPost}
              onClick={() => {
                if (card.action.type === "navigate") navigate(card.action.to);
                else setTab(card.action.tab);
              }}
            />
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500">Latest from marketplace</h2>
            <div className="flex gap-2 bg-slate-100 rounded-full p-1 ml-auto sm:ml-0">
              <button
                onClick={() => setTab("open")}
                data-testid="tab-open"
                className={cn(
                  "px-3.5 py-1 rounded-full text-xs font-medium transition-colors",
                  tab === "open" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                )}
              >
                <Globe2 className="h-3 w-3 inline mr-1 -mt-0.5" /> Open
              </button>
              <button
                onClick={() => setTab("mine")}
                data-testid="tab-mine"
                className={cn(
                  "px-3.5 py-1 rounded-full text-xs font-medium transition-colors",
                  tab === "mine" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                )}
              >
                <Gavel className="h-3 w-3 inline mr-1 -mt-0.5" /> Mine
              </button>
            </div>
            <div className="relative flex-1 min-w-[160px] sm:ml-auto">
              <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search…"
                className="pl-8 h-8 text-sm"
                data-testid="tender-search"
              />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
            {loading ? (
              <p className="text-slate-500 text-sm p-6">Loading…</p>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center">
                <Globe2 className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  {q ? "No tenders match your search."
                    : tab === "open" ? "No open tenders from other organizations right now."
                    : "Your org hasn't posted any tenders yet."}
                </p>
              </div>
            ) : (
              filtered.map((t) => <FeedRow key={t.id} t={t} />)
            )}
          </div>
        </div>

        <div className="space-y-4">
          <LeaderboardWidget myOrgId={org?.id} />
          <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 flex items-start gap-2.5">
            <Sparkles className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-900">
              Every completed tender feeds your org's marketplace rank — deliver on time to
              climb faster than orgs that just post volume.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
